/**
 * ClonePreviewModal — v138.5 (Feb 2026)
 *
 * Reusable clone-preview UI. Used both from the public demo bio pages
 * (`/u/demo-*`) and from inside the Bio Hub designer's "Examples"
 * browser (Action Hub → Bio → Examples).
 *
 * Props
 *   creator        — { name, ... } — used only for the header text
 *   hubTheme       — string theme id (renders capitalised)
 *   hubBlocks      — Array<{type, block_id?}> — the demo's blocks
 *   userPlanId     — "launch" | "growth" | "scale" (drives upsell banner)
 *   cloning        — boolean — disables the action buttons
 *   onClose()      — dismiss
 *   onConfirm(replace: boolean)  — commit (replace or append)
 */

// v146.34 — removed react-router Link (was only used by the upgrade
// banner that we no longer show; silent drop preserves clean UX).
import { Sparkles } from "lucide-react";

export const BLOCK_LABELS = {
  avatar:            { label: "Profile header",              icon: "👤" },
  text:              { label: "Text block",                  icon: "✍️" },
  featured_product:  { label: "Featured product / service",  icon: "🛒" },
  storefront:        { label: "Storefront grid",             icon: "🏪" },
  paid_coupon:       { label: "Paid coupon unlock",          icon: "🔓" },
  video:             { label: "Video reel",                  icon: "▶️" },
  testimonial:       { label: "Testimonial",                 icon: "⭐" },
  google_review:     { label: "Google reviews",              icon: "🌟" },
  whatsapp:          { label: "WhatsApp chat",               icon: "💬" },
  contact_card:      { label: "Contact card (business card)", icon: "📇" },
  image_grid:        { label: "Image grid (menu / catalog)",  icon: "🖼️" },
  video_grid:        { label: "Video grid (reels)",           icon: "🎬" },
  location:          { label: "Location / map",              icon: "📍" },
  faq:               { label: "FAQs",                        icon: "❓" },
  lead_form:         { label: "Lead capture form",           icon: "📝" },
  socials:           { label: "Social links",                icon: "🔗" },
  appointment:       { label: "Appointment booking",         icon: "📅" },
  countdown:         { label: "Countdown timer",             icon: "⏳" },
  newsletter:        { label: "Newsletter signup",           icon: "📧" },
  waitlist:          { label: "Waitlist",                    icon: "📋" },
  page_gate:         { label: "Page gate",                   icon: "🔒" },
  affiliate_picks:   { label: "Affiliate picks",             icon: "🤝" },
  ads:               { label: "Ads (earn from local ads)", icon: "📣" },
};

// v138.4 — Block-level plan gating. The minimum Action Hub tier a block
// needs to render its full capability. Blocks not in this map are
// available on every plan tier.
//   launch (Starter) < growth (Influencer) < scale (Brand)
export const BLOCK_PLAN_MIN = {
  affiliate_picks:  "growth",
  paid_coupon:      "growth",
  page_gate:        "scale",
};
export const PLAN_RANK = { launch: 0, growth: 1, scale: 2 };
export const PLAN_LABEL = { launch: "Personal", growth: "Creator", scale: "Brand" };

export default function ClonePreviewModal({
  creator, hubTheme, hubBlocks, cloning, userPlanId,
  onClose, onConfirm,
}) {
  // v146.34 — Silently drop plan-locked blocks so the wizard only shows
  // (and clones) what the user's plan actually supports. Backend also
  // drops server-side as a safety net.
  const currentRank = PLAN_RANK[userPlanId] ?? 0;
  const isUsable = (b) => {
    const need = BLOCK_PLAN_MIN[b.type];
    return !need || (PLAN_RANK[need] ?? 0) <= currentRank;
  };
  const usableBlocks = hubBlocks.filter(isUsable);
  const droppedCount = hubBlocks.length - usableBlocks.length;
  const blockCount = usableBlocks.length;
  const typeSet = Array.from(new Set(usableBlocks.map((b) => b.type)));

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !cloning) onClose(); }}
      onTouchEnd={(e) => { if (e.target === e.currentTarget && !cloning) onClose(); }}
      data-testid="clone-preview-modal"
    >
      <div
        className="bg-white border-4 border-ink shadow-brutal w-full max-w-md max-h-[92dvh] flex flex-col rounded-t-2xl sm:rounded-none"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {/* Header — sticky at top */}
        <div className="border-b-2 border-ink bg-sun p-4 flex items-start gap-3 shrink-0">
          <div className="w-11 h-11 border-2 border-ink bg-coral text-white flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display font-black text-lg tracking-tight leading-tight">
              Preview your new hub
            </p>
            <p className="text-xs text-ink/70 mt-0.5">
              Cloning from <span className="font-bold">{creator.name}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (!cloning) onClose(); }}
            data-testid="clone-preview-close"
            aria-label="Close preview"
            className="border-2 border-ink bg-white w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-brutalSm"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-4 flex-1 overflow-y-auto min-h-0">
          {/* v146.34 — Silent drop notice. Only shown when the source
              template contained tier-restricted blocks we skipped. No
              upgrade banner, no gate pills — user gets a clean preview
              of only what will actually clone. */}
          {droppedCount > 0 && (
            <p className="text-[11px] text-ink/60 leading-snug" data-testid="clone-preview-silent-drop">
              {droppedCount} block{droppedCount === 1 ? "" : "s"} from this
              template need{droppedCount === 1 ? "s" : ""} a higher plan and
              will be skipped. The rest will clone normally.
            </p>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="border-2 border-ink bg-cream p-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-0.5">Theme</p>
              <p className="font-display font-black text-base capitalize">{hubTheme}</p>
            </div>
            <div className="border-2 border-ink bg-cream p-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-0.5">Blocks</p>
              <p className="font-display font-black text-base">{blockCount} block{blockCount === 1 ? "" : "s"}</p>
            </div>
          </div>

          <div>
            <p className="text-[11px] font-black uppercase tracking-wider text-ink/60 mb-2">You&apos;ll get</p>
            <div className="border-2 border-ink bg-white divide-y-2 divide-ink" data-testid="clone-preview-block-list">
              {usableBlocks.map((b, i) => {
                const meta = BLOCK_LABELS[b.type] || { label: b.type, icon: "•" };
                return (
                  <div key={b.block_id || i} className="px-3 py-2 flex items-center gap-2 text-sm">
                    <span className="text-lg leading-none flex-shrink-0" aria-hidden>{meta.icon}</span>
                    <span className="font-bold flex-1 truncate">{meta.label}</span>
                    <span className="text-[10px] font-mono text-ink/40">#{i + 1}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-ink/60 mt-2 leading-snug">
              {/* v146.81 — Products ARE now copied when the demo has a
                  products_grid / storefront / featured_product block AND
                  your catalog is empty. Edit or delete them anytime from
                  Commerce. */}
              Demo <span className="font-bold">products &amp; images</span> are
              copied into your catalog so this page renders real items —
              you can edit or delete them later from{" "}
              <span className="font-bold">Commerce</span>. Coupons and
              services stay as demo placeholders.
            </p>
          </div>

          {typeSet.length > 4 && (
            <div className="flex flex-wrap gap-1.5">
              {typeSet.slice(0, 12).map((t) => {
                const m = BLOCK_LABELS[t] || { label: t };
                return (
                  <span key={t} className="text-[10px] font-bold border border-ink bg-cream px-1.5 py-0.5">
                    {m.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t-2 border-ink bg-cream p-4 space-y-2 shrink-0 pb-[calc(1rem+env(safe-area-inset-bottom,0))]">
          <p className="text-[11px] text-ink/70 leading-snug">
            <span className="font-black">Heads up:</span>{" "}
            <span className="font-bold">Replace my hub</span> wipes your
            current blocks first. <span className="font-bold">Add on top</span>{" "}
            keeps them and appends the demo below.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onConfirm(true); }}
              disabled={cloning}
              data-testid="clone-preview-confirm-replace"
              className="flex-1 border-2 border-ink bg-coral text-white px-3 py-2.5 font-black text-sm uppercase tracking-wider shadow-brutalSm disabled:opacity-60"
            >
              {cloning ? "Cloning…" : "Replace my hub"}
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onConfirm(false); }}
              disabled={cloning}
              data-testid="clone-preview-confirm-append"
              className="flex-1 border-2 border-ink bg-white text-ink px-3 py-2.5 font-black text-sm uppercase tracking-wider shadow-brutalSm disabled:opacity-60"
            >
              Add on top
            </button>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (!cloning) onClose(); }}
            disabled={cloning}
            data-testid="clone-preview-cancel"
            className="w-full text-center text-[11px] font-bold text-ink/60 underline pt-1"
          >
            Keep my current hub
          </button>
        </div>
      </div>
    </div>
  );
}
